import { useState, useEffect } from 'react';
import { apiClient } from '../../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_CARD, BG_SURFACE } from './designTokens';

interface DailyReportData {
  date: string;
  totalActions: number;
  actionCounts: Record<string, number>;
  topActors: { name: string; count: number }[];
  hourlyDistribution: number[];
  aiSummary: string;
  uniqueActors: number;
  entries?: AuditEntry[];
}

interface AuditEntry {
  id: string;
  action: string;
  userId: string;
  userName: string;
  tableId: string | null;
  seatId: string | null;
  guestId: string | null;
  previousValue: string | null;
  newValue: string | null;
  metadata: string | null;
  createdAt: string;
  guestName?: string | null;
  tableLabel?: string | null;
  seatLabel?: string | null;
}

const ACTION_LABELS: Record<string, string> = {
  SeatAssigned: 'Seat Assigned',
  SeatUnassigned: 'Seat Unassigned',
  SeatBlocked: 'Seat Blocked',
  SeatReleased: 'Seat Released',
  SeatCheckedIn: 'Checked In',
  CheckInReversed: 'Check-In Reversed',
  TableCreated: 'Table Created',
  TableUpdated: 'Table Updated',
  TableBlockApplied: 'Table Block Applied',
  TableBlockReleased: 'Table Block Released',
  SoftBlockCreated: 'Soft Block Created',
  SoftBlockExpired: 'Soft Block Expired',
  SoftBlockReleased: 'Soft Block Released',
  SoftBlockConverted: 'Soft Block Converted',
  HardBlockCreated: 'Hard Block Created',
  HardBlockModified: 'Hard Block Modified',
  HardBlockReleased: 'Hard Block Released',
  GuestCreated: 'Guest Created',
  GuestUpdated: 'Guest Updated',
  ZoneReassigned: 'Zone Reassigned',
};

const ACTION_COLORS: Record<string, string> = {
  SeatAssigned: '#166534',
  SeatUnassigned: '#991b1b',
  SeatCheckedIn: '#1e40af',
  CheckInReversed: '#9333ea',
  ZoneReassigned: '#ca8a04',
  SeatBlocked: '#dc2626',
  SeatReleased: '#16a34a',
};

/** Convert audit entry into a human-readable description using enriched fields */
function describeEntry(entry: AuditEntry): string {
  const { action, guestName, tableLabel, seatLabel } = entry;
  const guest = guestName || 'a guest';
  const table = tableLabel || '';
  const seat = seatLabel || '';

  switch (action) {
    case 'SeatAssigned':
      return `Assigned ${guest}${table ? ` to ${table}` : ''}${seat ? `, ${seat}` : ''}`;
    case 'SeatUnassigned':
      return `Removed ${guest}${table ? ` from ${table}` : ''}`;
    case 'SeatCheckedIn':
      return `Checked in ${guest}${table ? ` at ${table}` : ''}`;
    case 'CheckInReversed':
      return `Reversed check-in for ${guest}`;
    case 'SeatBlocked':
    case 'HardBlockCreated':
    case 'SoftBlockCreated':
      return `Blocked${seat ? ` ${seat}` : ''}${table ? ` on ${table}` : ''}`;
    case 'SeatReleased':
    case 'HardBlockReleased':
    case 'SoftBlockReleased':
      return `Released block${seat ? ` on ${seat}` : ''}${table ? ` (${table})` : ''}`;
    case 'SoftBlockExpired':
      return `Soft block expired${seat ? ` on ${seat}` : ''}`;
    case 'SoftBlockConverted':
      return `Converted soft → hard block${seat ? ` on ${seat}` : ''}`;
    case 'HardBlockModified':
      return `Modified hard block${seat ? ` on ${seat}` : ''}`;
    case 'TableCreated':
      return `Created ${table || 'new table'}`;
    case 'TableUpdated':
      return `Updated ${table || 'table'}`;
    case 'TableBlockApplied':
      return `Applied block to ${table || 'table'}`;
    case 'TableBlockReleased':
      return `Released block on ${table || 'table'}`;
    case 'ZoneReassigned':
      return `Moved ${table || 'table'} to new zone`;
    case 'GuestCreated':
      return `Created guest record: ${guest}`;
    case 'GuestUpdated':
      return `Updated guest record: ${guest}`;
    default:
      return ACTION_LABELS[action] || action;
  }
}

export function DailyActivityReport() {
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [data, setData] = useState<DailyReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [error, setError] = useState('');
  const [fetchKey, setFetchKey] = useState(0);

  async function fetchReport(reportDate: string, detail: boolean) {
    setLoading(true);
    setError('');
    try {
      const result = await apiClient<DailyReportData>(
        `/table-management/audit-log/daily-report?date=${reportDate}&detail=${detail}`
      );
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }

  // Fetch on mount (tab switch), date change, detail toggle, or manual refresh
  useEffect(() => {
    fetchReport(date, showDetail);
  }, [date, showDetail, fetchKey]);

  const handlePrint = () => {
    if (!data) return;
    const actionRows = Object.entries(data.actionCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([action, count]) => `<tr><td>${ACTION_LABELS[action] || action}</td><td style="text-align:center;">${count}</td></tr>`)
      .join('');

    const detailRows = data.entries
      ? data.entries.map((e) => {
          const time = new Date(e.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          return `<tr><td>${time}</td><td>${ACTION_LABELS[e.action] || e.action}</td><td>${e.userName}</td><td style="font-size:10px;color:#666;">${describeEntry(e)}</td></tr>`;
        }).join('')
      : '';

    const html = `<!DOCTYPE html><html><head>
      <title>Daily Seat Activity Report — ${data.date}</title>
      <style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;padding:24px 32px;color:#1f2937;line-height:1.5;font-size:13px;}h1{font-size:20px;font-weight:700;margin-bottom:4px;color:#0f2d1f;}p.sub{color:#6b7280;font-size:12px;margin-bottom:16px;}table{width:100%;border-collapse:collapse;border:1px solid #e5e7eb;margin-bottom:20px;}th{text-align:left;padding:6px 10px;border:1px solid #e5e7eb;font-size:10px;font-weight:600;background:#f3f4f6;color:#374151;text-transform:uppercase;}td{padding:5px 10px;border:1px solid #e5e7eb;font-size:12px;vertical-align:top;}tr:nth-child(even){background:#f9fafb;}.summary{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:12px 16px;margin-bottom:16px;font-size:13px;line-height:1.6;}@media print{body{padding:12px 16px;}}</style>
    </head><body>
      <h1>Daily Seat Activity Report</h1>
      <p class="sub">${data.date} &bull; ${data.totalActions} actions by ${data.uniqueActors} users &bull; Printed ${new Date().toLocaleDateString()}</p>
      <div class="summary">${data.aiSummary}</div>
      <h3 style="font-size:14px;margin-bottom:8px;">Activity Breakdown</h3>
      <table><thead><tr><th>Action</th><th style="text-align:center;">Count</th></tr></thead><tbody>${actionRows}</tbody></table>
      ${data.entries ? `<h3 style="font-size:14px;margin-bottom:8px;">Detailed Transactions (${data.entries.length})</h3><table><thead><tr><th>Time</th><th>Action</th><th>User</th><th>Details</th></tr></thead><tbody>${detailRows}</tbody></table>` : ''}
    </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.82rem', fontWeight: 600, color: TEXT_DARK }}>Date:</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={{ padding: '0.4rem 0.6rem', fontSize: '0.85rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px' }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: TEXT_MUTED, cursor: 'pointer' }}>
          <input type="checkbox" checked={showDetail} onChange={(e) => setShowDetail(e.target.checked)} />
          Include detailed transactions
        </label>
        <button
          onClick={() => setFetchKey((k) => k + 1)}
          style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 500, backgroundColor: '#e5e7eb', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          🔄 Refresh
        </button>
        <button
          onClick={handlePrint}
          disabled={!data || data.totalActions === 0}
          style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: data && data.totalActions > 0 ? 1 : 0.5 }}
        >
          🖨️ Print Report
        </button>
      </div>

      {loading && <p style={{ color: TEXT_MUTED, fontSize: '0.85rem' }}>Loading report...</p>}
      {error && <p style={{ color: '#d32f2f', fontSize: '0.82rem', backgroundColor: '#fdecea', padding: '0.5rem', borderRadius: '4px' }}>{error}</p>}

      {data && !loading && (
        <>
          {/* AI Summary */}
          <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.85rem', lineHeight: '1.6', color: '#166534' }}>
            <strong style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>AI Activity Summary</strong>
            <p style={{ margin: '4px 0 0 0' }}>{data.aiSummary}</p>
          </div>

          {data.totalActions === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: TEXT_MUTED, backgroundColor: BG_SURFACE, borderRadius: '8px' }}>
              No seat activity recorded for {data.date}
            </div>
          ) : (
            <>
              {/* Summary pills */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {Object.entries(data.actionCounts).sort((a, b) => b[1] - a[1]).map(([action, count]) => (
                  <div key={action} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 700, color: ACTION_COLORS[action] || TEXT_DARK }}>{count}</span>
                    <span style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>{ACTION_LABELS[action] || action}</span>
                  </div>
                ))}
              </div>

              {/* Hourly chart */}
              <div style={{ backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', padding: '0.75rem 1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.5rem', textTransform: 'uppercase' }}>Hourly Activity</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '60px' }}>
                  {data.hourlyDistribution.map((count, hour) => {
                    const max = Math.max(...data.hourlyDistribution, 1);
                    const pct = (count / max) * 100;
                    return (
                      <div key={hour} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: '100%', height: `${pct}%`, minHeight: count > 0 ? '3px' : '0', backgroundColor: count > 0 ? PRIMARY : '#e5e7eb', borderRadius: '2px 2px 0 0' }} title={`${hour}:00 — ${count} actions`} />
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                  <span style={{ fontSize: '0.6rem', color: TEXT_MUTED }}>0h</span>
                  <span style={{ fontSize: '0.6rem', color: TEXT_MUTED }}>6h</span>
                  <span style={{ fontSize: '0.6rem', color: TEXT_MUTED }}>12h</span>
                  <span style={{ fontSize: '0.6rem', color: TEXT_MUTED }}>18h</span>
                  <span style={{ fontSize: '0.6rem', color: TEXT_MUTED }}>23h</span>
                </div>
              </div>

              {/* Top actors */}
              {data.topActors.length > 0 && (
                <div style={{ backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', padding: '0.75rem 1rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.4rem', textTransform: 'uppercase' }}>Top Users</div>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {data.topActors.map((actor) => (
                      <span key={actor.name} style={{ fontSize: '0.8rem', color: TEXT_DARK }}>
                        <strong>{actor.name}</strong> <span style={{ color: TEXT_MUTED }}>({actor.count})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Detail table */}
              {showDetail && data.entries && data.entries.length > 0 && (
                <div style={{ backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', padding: '0.75rem 1rem', overflowX: 'auto' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                    Detailed Transactions ({data.entries.length})
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `2px solid #d1d5db`, fontSize: '0.7rem', fontWeight: 600, color: TEXT_DARK, textTransform: 'uppercase' }}>Time</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `2px solid #d1d5db`, fontSize: '0.7rem', fontWeight: 600, color: TEXT_DARK, textTransform: 'uppercase' }}>Action</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `2px solid #d1d5db`, fontSize: '0.7rem', fontWeight: 600, color: TEXT_DARK, textTransform: 'uppercase' }}>User</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `2px solid #d1d5db`, fontSize: '0.7rem', fontWeight: 600, color: TEXT_DARK, textTransform: 'uppercase' }}>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.entries.map((entry, idx) => {
                        const time = new Date(entry.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                        return (
                          <tr key={entry.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                            <td style={{ padding: '5px 8px', borderBottom: `1px solid ${BORDER_LIGHT}`, whiteSpace: 'nowrap' }}>{time}</td>
                            <td style={{ padding: '5px 8px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: ACTION_COLORS[entry.action] || TEXT_DARK, fontWeight: 500 }}>{ACTION_LABELS[entry.action] || entry.action}</td>
                            <td style={{ padding: '5px 8px', borderBottom: `1px solid ${BORDER_LIGHT}` }}>{entry.userName}</td>
                            <td style={{ padding: '5px 8px', borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.75rem', color: TEXT_MUTED, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{describeEntry(entry)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
